import { useMemo, useState } from 'react';
import { Bell, ChevronRight } from 'lucide-react';
import { CarteiraTimelineSheet } from '@/components/carteira/CarteiraTimelineSheet';

/**
 * CHAMADO DE CARTEIRA na tela do consultor.
 *
 * Este alerta NÃO resolve nada — é só a batida na porta. A ação inteira mora na
 * timeline do cliente: clicou, abre a MESMA tela onde o chamado foi escrito,
 * lê e responde ali. Responder encerra o aviso, então o item some sozinho
 * daqui (o `useAvisosUsuario` ouve `avisos` por realtime).
 *
 * Mesma régua da transferência (Douglas 26/07): UM alerta com o total, cliente
 * a cliente, sem "confirmar tudo" — que seria o jeito mais rápido de zerar a
 * tela sem atender ninguém.
 */

export interface AvisoChamadoCarteira {
  id: string;
  metadados?: Record<string, unknown> | null;
}

interface Props {
  avisos: AvisoChamadoCarteira[];
}

const texto = (valor: unknown) => (valor == null ? '' : String(valor));

const formatarCnpj = (valor: string) => {
  const d = (valor || '').replace(/\D/g, '');
  if (d.length !== 14) return valor;
  return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}`;
};

export function AvisosChamadoCarteiraAgrupado({ avisos }: Props) {
  const [aberto, setAberto] = useState(true);
  const [alvo, setAlvo] = useState<{ cnpj: string; clienteId: string | null; razaoSocial: string | null } | null>(null);

  const itens = useMemo(
    () =>
      avisos.map((aviso) => {
        const meta = (aviso.metadados || {}) as Record<string, unknown>;
        return {
          id: aviso.id,
          cnpj: texto(meta.cnpj),
          clienteId: meta.cliente_id ? texto(meta.cliente_id) : null,
          razaoSocial: meta.razao_social ? texto(meta.razao_social) : null,
          pedido: texto(meta.texto),
          autor: texto(meta.autor_nome) || 'Alguém',
        };
      }),
    [avisos],
  );

  if (!itens.length) return null;

  if (!aberto) {
    return (
      <button
        type="button"
        onClick={() => setAberto(true)}
        className="fixed bottom-4 right-4 z-50 flex items-center gap-2 rounded-full bg-[linear-gradient(90deg,#4A1320,#5B0A0D)] px-4 py-3 text-sm font-semibold text-white shadow-xl dark:bg-[linear-gradient(90deg,#9D2530,#6C2236)]"
      >
        <Bell className="h-4 w-4" />
        Clientes esperando você
        <span className="rounded-full bg-white/20 px-1.5 tabular-nums">{itens.length}</span>
      </button>
    );
  }

  return (
    <>
      <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/55 p-4">
        <section className="w-full max-w-[560px] overflow-hidden rounded-xl border border-border bg-card shadow-2xl">
          <header className="flex h-12 items-center gap-2 border-b border-border bg-[#FAF8F4] px-4 dark:bg-[#241A1A]">
            <Bell className="h-4 w-4 text-[#5B0A0D] dark:text-[#C87484]" />
            <div className="min-w-0 flex-1">
              <h2 className="truncate text-[10px] font-bold uppercase tracking-[1.2px] text-foreground">
                Chamados de carteira
              </h2>
              <p className="truncate text-[10.5px] text-[#6B5A4D] dark:text-[#B5A8A8]">
                Alguém pediu que você atenda estes clientes
              </p>
            </div>
            <span className="text-xs font-bold tabular-nums text-[#5B0A0D] dark:text-[#C87484]">{itens.length}</span>
            <button
              type="button"
              onClick={() => setAberto(false)}
              className="rounded px-2 py-1 text-[11px] text-[#6B5A4D] hover:bg-muted dark:text-[#B5A8A8]"
            >
              Depois
            </button>
          </header>

          <div className="max-h-[60vh] overflow-y-auto">
            {itens.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => setAlvo({ cnpj: item.cnpj, clienteId: item.clienteId, razaoSocial: item.razaoSocial })}
                className="flex w-full items-center gap-3 border-b border-border px-4 py-3 text-left transition-colors last:border-b-0 hover:bg-[#F4F0EA] dark:hover:bg-[#2A1F1F]"
              >
                <span aria-hidden className="h-9 w-[3px] shrink-0 rounded-full bg-[#5B0A0D] dark:bg-[#9D2530]" />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[13.5px] font-semibold text-foreground">
                    {item.razaoSocial || formatarCnpj(item.cnpj)}
                  </span>
                  <span className="block truncate text-[11.5px] text-foreground/80">{item.pedido}</span>
                  <span className="block truncate text-[10px] text-[#6B5A4D] dark:text-[#B5A8A8]">
                    pedido por {item.autor}
                  </span>
                </span>
                <ChevronRight className="h-4 w-4 shrink-0 text-[#6B5A4D] dark:text-[#B5A8A8]" />
              </button>
            ))}
          </div>

          <footer className="flex h-10 items-center border-t border-border px-4">
            <span className="text-[10.5px] text-[#6B5A4D] dark:text-[#B5A8A8]">
              Abre a timeline do cliente. Responder lá encerra o chamado.
            </span>
          </footer>
        </section>
      </div>

      {alvo && (
        <CarteiraTimelineSheet
          open
          onOpenChange={(estaAberto) => { if (!estaAberto) setAlvo(null); }}
          cnpj={alvo.cnpj}
          clienteId={alvo.clienteId}
          razaoSocial={alvo.razaoSocial}
        />
      )}
    </>
  );
}
